import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { AxiosError } from "axios";
import {
  MutedAlertCameras,
  MutedAlertSites,
} from "../AlertsUtils/alertsInterfaces";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import {
  isAlertOutcomeForbidden,
  revokeCachedAlertOutcomePermission,
} from "../AlertsUtils/alertOutcomePermission";
import { mapSharedMutes } from "../AlertsUtils/mapSharedMutes";
import { logger } from "../../../../utils/logger";

// Latest fetchSharedMutes request; older responses are dropped.
let sharedMutesRequestSeq = 0;

// Camera whose notes were last requested; a slower response for a camera the
// user has already stepped past is dropped (ENG-2421).
let cameraNotesRequestedFor: number | null = null;

interface AlertsState {
  // Site ID state
  siteId: number;
  setSiteId: (siteId: number) => void;

  // Site notes state
  siteNotes: string;
  setSiteNotes: (notes: string) => void;

  // Dialog state
  viewSiteProtocolDialogOpen: boolean;
  setViewSiteProtocolDialogOpen: (open: boolean) => void;

  // Loading state
  retrievingSiteNotes: boolean;
  setRetrievingSiteNotes: (loading: boolean) => void;

  notesCache: any;
  setNotesCache: (notesCache: any) => void;

  // ENG-2421: the selected alert's camera notes, from the camera's
  // general_info/. Shown beside the site notes in the protocol dialog.
  cameraNotes: string;
  retrievingCameraNotes: boolean;
  cameraNotesCache: Record<string, string>;

  // ENG-2135: integration_type_name per site, from the same about/ response
  // the notes come from. Undefined until that site has been fetched.
  siteIntegrationTypes: Record<string, string | null>;

  mutedAlertsDialogOpen: boolean;
  setMutedAlertsDialogOpen: (open: boolean) => void;

  mutedAlertSites: MutedAlertSites;
  setMutedAlertSites: (mutedAlertSites: MutedAlertSites) => void;

  mutedAlertCameras: MutedAlertCameras;
  setMutedAlertCameras: (mutedAlertCameras: MutedAlertCameras) => void;

  seeMutedSiteAndCamerasDialogOpen: boolean;
  setSeeMutedSiteAndCamerasDialogOpen: (open: boolean) => void;

  alertState: {
    cameraMutedSuccess: boolean;
    siteMutedSuccess: boolean;
  };
  setAlertState: (alertState: {
    cameraMutedSuccess: boolean;
    siteMutedSuccess: boolean;
  }) => void;

  mutedAlertInfo: {
    siteName: string;
    cameraName: string;
    alertEndTime: number;
  };

  setMutedAlertInfo: (mutedAlertInfo: {
    siteName: string;
    cameraName: string;
    alertEndTime: number;
  }) => void;

  selectedRowIsMuted: boolean;
  setSelectedRowIsMuted: (selectedRowIsMuted: boolean) => void;

  fetchSharedMutes: (navigate: NavigateFunction) => Promise<void>;

  unmuteSharedMute: (
    scope: "site" | "camera",
    entry: { site_id?: number; camera_id?: number },
    navigate: NavigateFunction
  ) => Promise<boolean>;

  handleSubmitMutedAlert: (
    selectedRow: any,
    navigate: NavigateFunction
  ) => Promise<void>;

  // API call to get site notes
  getSiteNotes: (siteId: number, navigate: NavigateFunction) => Promise<void>;

  getCameraNotes: (
    cameraId: number,
    navigate: NavigateFunction
  ) => Promise<void>;

  muteAlertsError: string;
  setMuteAlertsError: (muteAlertsError: string) => void;

  handleMuteAlerts: (
    alertType: string,
    muteType: string,
    cameraId: number | string | null | undefined,
    durationMinutes: number,
    navigate: NavigateFunction
  ) => Promise<boolean>;

  resetAlertsStore: () => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  // Initial state
  siteId: 0,
  setSiteId: (siteId: number) => set({ siteId: siteId }),

  siteNotes: "",
  setSiteNotes: (notes: string) => set({ siteNotes: notes }),

  viewSiteProtocolDialogOpen: false,
  setViewSiteProtocolDialogOpen: (open: boolean) =>
    set({ viewSiteProtocolDialogOpen: open }),

  retrievingSiteNotes: false,
  setRetrievingSiteNotes: (loading: boolean) =>
    set({ retrievingSiteNotes: loading }),

  notesCache: {},
  setNotesCache: (notesCache: any) => set({ notesCache }),

  cameraNotes: "",
  retrievingCameraNotes: false,
  cameraNotesCache: {},

  siteIntegrationTypes: {},

  mutedAlertsDialogOpen: false,
  setMutedAlertsDialogOpen: (open: boolean) =>
    set({ mutedAlertsDialogOpen: open }),

  mutedAlertSites: [],
  setMutedAlertSites: (mutedAlertSites: MutedAlertSites) =>
    set({ mutedAlertSites }),

  mutedAlertCameras: [],
  setMutedAlertCameras: (mutedAlertCameras: MutedAlertCameras) =>
    set({ mutedAlertCameras }),

  seeMutedSiteAndCamerasDialogOpen: false,
  setSeeMutedSiteAndCamerasDialogOpen: (open: boolean) =>
    set({ seeMutedSiteAndCamerasDialogOpen: open }),

  alertState: {
    cameraMutedSuccess: false,
    siteMutedSuccess: false,
  },
  setAlertState: (alertState: {
    cameraMutedSuccess: boolean;
    siteMutedSuccess: boolean;
  }) => set({ alertState }),

  mutedAlertInfo: {
    siteName: "",
    cameraName: "",
    alertEndTime: 0,
  },

  setMutedAlertInfo: (mutedAlertInfo: {
    siteName: string;
    cameraName: string;
    alertEndTime: number;
  }) => set({ mutedAlertInfo }),

  selectedRowIsMuted: false,
  setSelectedRowIsMuted: (selectedRowIsMuted: boolean) =>
    set({ selectedRowIsMuted }),

  // Shared (fleet-wide) mute list, ENG-2103. Mutes are operational and apply
  // to every operator, so the list is read from monitoring-api rather than
  // the muting user's own storage/user record (which left other operators
  // seeing "No muted sites" — SUP-727). Called on page load, on the page's
  // 30 s tick, when the see-muted dialog opens, and after every mute/unmute.
  // Failures keep the last known lists; this never throws.
  fetchSharedMutes: async (navigate: NavigateFunction) => {
    const requestSeq = ++sharedMutesRequestSeq;

    const AxiosUI = createMonitoringAPICall(``);

    const retryFunction = () => {
      get().fetchSharedMutes(navigate);
    };

    try {
      const response = await AxiosUI.get(`streaming/mutes/`);
      // A slower, older response must not overwrite a newer one (e.g. the
      // tick's fetch landing after the post-unmute refetch).
      if (requestSeq !== sharedMutesRequestSeq) return;
      const { sites, cameras } = mapSharedMutes(response.data);
      set({ mutedAlertSites: sites, mutedAlertCameras: cameras });
    } catch (reason) {
      const error = reason as AxiosError;
      // Only an auth failure is worth handing on (token refresh + retry);
      // anything else is a background read the user never asked for.
      if (error?.response?.status === 403) {
        universalAPIErrorHandler(error, navigate, retryFunction, () => {});
      } else {
        logger.warn("Failed to fetch shared mutes:", error?.message);
      }
    }
  },

  // Unmute a row of the shared list, then refetch it. A site-scope unmute
  // goes through any camera of the site, which the shared list carries on
  // each site row (monitoring-api #220).
  unmuteSharedMute: async (
    scope: "site" | "camera",
    entry: { site_id?: number; camera_id?: number },
    navigate: NavigateFunction
  ) => {
    const { handleMuteAlerts, fetchSharedMutes, setMuteAlertsError } = get();

    const cameraId = entry.camera_id;
    if (cameraId == null) {
      setMuteAlertsError(
        "Unable to unmute: this site has no cameras. Please refresh and try again."
      );
      return false;
    }

    const unmuted = await handleMuteAlerts(
      scope,
      "unmute",
      cameraId,
      0,
      navigate
    );
    if (unmuted) await fetchSharedMutes(navigate);
    return unmuted;
  },

  // API call to get site notes/protocol
  getSiteNotes: async (siteId: number, navigate: NavigateFunction) => {
    const { setRetrievingSiteNotes, setSiteNotes, notesCache, setNotesCache } =
      get();

    setSiteNotes("");

    const cachedNoteKeys = Object.keys(notesCache);

    if (cachedNoteKeys.includes(siteId.toString())) {
      setSiteNotes(notesCache[siteId]);
      return;
    }

    setRetrievingSiteNotes(true);

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getSiteNotes(siteId, navigate);
    };

    try {
      const response = await AxiosUI.get(`customer/${siteId}/about/`);

      // Extract notes from the response - adjust this based on actual API response structure
      const notes = response.data?.notes || response.data?.instructions || "";
      setSiteNotes(notes);
      setNotesCache({ ...notesCache, [siteId]: notes });
      // Same response carries the site's integration type; the talk-down
      // control (ENG-2135) gates on it. Cached alongside the notes.
      set({
        siteIntegrationTypes: {
          ...get().siteIntegrationTypes,
          [siteId]: response.data?.integration_type_name ?? null,
        },
      });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setSiteNotes("");
        }
      );
    } finally {
      setRetrievingSiteNotes(false);
    }
  },

  getCameraNotes: async (cameraId: number, navigate: NavigateFunction) => {
    cameraNotesRequestedFor = cameraId;
    const cached = get().cameraNotesCache[cameraId];
    if (cached !== undefined) {
      set({ cameraNotes: cached, retrievingCameraNotes: false });
      return;
    }

    set({ cameraNotes: "", retrievingCameraNotes: true });

    const AxiosUI = createApi(``);

    const retryFunction = () => {
      get().getCameraNotes(cameraId, navigate);
    };

    try {
      const response = await AxiosUI.get(`/camera/${cameraId}/general_info/`);
      const notes = response.data?.notes || "";
      set({
        cameraNotesCache: { ...get().cameraNotesCache, [cameraId]: notes },
      });
      if (cameraNotesRequestedFor === cameraId) set({ cameraNotes: notes });
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          if (cameraNotesRequestedFor === cameraId) set({ cameraNotes: "" });
        }
      );
    } finally {
      if (cameraNotesRequestedFor === cameraId) {
        set({ retrievingCameraNotes: false });
      }
    }
  },

  muteAlertsError: "",
  setMuteAlertsError: (muteAlertsError: string) => set({ muteAlertsError }),

  // Operational mute: suppresses third-party alert dispatch (SMS/Immix/
  // Milestone/webhooks) at the connector for ALL operators via monitoring-api,
  // instead of the old per-user storage/user display filter.
  // For scope "site", any camera pk belonging to the site works as the URL
  // param — the backend expands site scope to all the site's cameras.
  // Returns true on success so callers can update local muted lists.
  handleMuteAlerts: async (
    alertType: string,
    muteType: string,
    cameraId: number | string | null | undefined,
    durationMinutes: number,
    navigate: NavigateFunction
  ): Promise<boolean> => {
    const { handleMuteAlerts, setAlertState, setMuteAlertsError } = get();

    if (cameraId === null || cameraId === undefined || cameraId === "") {
      setMuteAlertsError(
        "Unable to identify a camera for this mute request. Please refresh and try again."
      );
      return false;
    }

    const AxiosUI = createMonitoringAPICall(``);

    const retryFunction = () => {
      handleMuteAlerts(
        alertType,
        muteType,
        cameraId,
        durationMinutes,
        navigate
      );
    };

    const action = muteType === "mute" ? "mute" : "unmute";
    const body =
      action === "mute"
        ? { scope: alertType, duration_minutes: durationMinutes }
        : { scope: alertType };

    try {
      await AxiosUI.post(`streaming/${action}/${cameraId}/`, body);
      if (action === "mute") {
        setAlertState({
          cameraMutedSuccess: alertType === "camera",
          siteMutedSuccess: alertType === "site",
        });
      }
      return true;
    } catch (reason) {
      const error = reason as AxiosError;
      // Only a real denial ("you do not have permission to mute alerts") is a
      // permissions problem. An expired session also answers 403, and that
      // must renew the token and retry like every other request (ENG-1975).
      const detail = String(
        (error.response?.data as { detail?: unknown } | undefined)?.detail ?? ""
      );
      if (error.response?.status === 403 && /permission/i.test(detail)) {
        setMuteAlertsError(
          "You don't have permission to mute alerts. Contact an administrator to request alert-muting access."
        );
      } else {
        universalAPIErrorHandler(error, navigate, retryFunction, () => {});
      }
      return false;
    }
  },

  handleSubmitMutedAlert: async (
    selectedRow: any,
    navigate: NavigateFunction
  ) => {
    const { handleSubmitMutedAlert } = get();

    const postedAlert = {
      approx_capture_timestamp: selectedRow.approx_capture_timestamp,
      window_id: selectedRow.window_id,
      alert_outcome: null,
      threat_level: 1,
      notes: selectedRow.unresolved_notes,
    };

    const AxiosUI = createMonitoringAPICall(``);

    const retryFunction = () => {
      handleSubmitMutedAlert(selectedRow, navigate);
    };

    try {
      await AxiosUI.post(`alert/outcome/`, postedAlert);
    } catch (reason) {
      // A background write the user never clicked: on a set_alert_outcome
      // denial, drop the cached flag quietly (stops further attempts) rather
      // than alerting or retrying.
      if (isAlertOutcomeForbidden(reason)) {
        revokeCachedAlertOutcomePermission();
        return;
      }
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {}
      );
    }
  },

  resetAlertsStore: () => {
    set({
      siteId: 0,
      siteNotes: "",
      viewSiteProtocolDialogOpen: false,
      retrievingSiteNotes: false,
      notesCache: {},
      cameraNotes: "",
      retrievingCameraNotes: false,
      cameraNotesCache: {},
      siteIntegrationTypes: {},
      mutedAlertsDialogOpen: false,
      alertState: {
        cameraMutedSuccess: false,
        siteMutedSuccess: false,
      },
      mutedAlertSites: [],
      mutedAlertCameras: [],
      seeMutedSiteAndCamerasDialogOpen: false,
      muteAlertsError: "",
      mutedAlertInfo: {
        siteName: "",
        cameraName: "",
        alertEndTime: 0,
      },
      selectedRowIsMuted: false,
    });
  },
}));
